import { useMutation, useQuery } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { getData, postData } from '@/lib/api/client-v2';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import { ENDPOINTS } from '@/types/api/endpoints';
import type { Parrainage } from '@/types/api/parrainage';

import { useCurrentUser } from '@/hooks/api/use-user';

/**
 * Filleuls de l'utilisateur connecté (`GET /tiers/{id}/referrals`, `{id}` = `belair_id`).
 *
 * L'ancien écran relisait la liste à chaque montage.
 */
export const useParrainages = () => {
    const tiersId = useCurrentUser().belair_id;

    return useQuery({
        queryKey: queryKeys.parrainages(tiersId),
        queryFn: () => getData<Parrainage[]>(ENDPOINTS.tiersReferrals(String(tiersId))),
        refetchOnMount: 'always',
    });
};

export interface PostParrainageForm {
    text?: string;
    email?: string;
    phone?: string;
    index?: unknown;
}

/**
 * Message d'erreur du Pass, repris tel que l'ancien middleware le décodait : le message n'est
 * affiché que s'il était encodé en UTF-8 mal décodé (sinon la popin reste sans texte), ou brut si
 * le décodage échoue.
 */
const decodeErrorMessage = (raw: string | undefined): string | undefined => {
    let message: string | undefined;

    try {
        const encoded = escape(raw as string);
        const decoded = decodeURIComponent(encoded);

        if (raw !== decoded) {
            message = decoded;
        }
    } catch {
        message = raw ?? '';
    }

    return message;
};

/**
 * Envoi d'une invitation de parrainage (`POST /tiers/{id}/referrals`, délégué à la v1
 * `PostCreateFilleulRequest` : `email` ou `phone`).
 */
export const usePostParrainage = () => {
    const tiersId = useCurrentUser().belair_id;

    return useMutation({
        mutationFn: (form: PostParrainageForm) =>
            postData<null>(ENDPOINTS.tiersReferrals(String(tiersId)), { email: form.email, phone: form.phone }),
        onSuccess: (_data, form) => {
            const isPhone = !!form.phone;

            void notify({
                title: 'Félicitations !',
                icon: 'success',
                html: `Parrainage envoyé ! Votre filleul va recevoir par ${isPhone ? 'SMS' : 'email'} la proposition de parrainage`,
                confirmButtonText: "J'ai compris",
            });
        },
        onError: (error) => {
            const raw = getApiErrorMessage(error) || undefined;

            void notify({
                title: 'Attention !',
                icon: 'error',
                html: decodeErrorMessage(raw),
                confirmButtonText: "J'ai compris",
            });
        },
    });
};
